/**
 * Comparazione delle offerte di una gara, versioni dell'analisi, decisione.
 *
 * Sta sotto la sezione `acquisti_analisi`, non `acquisti_gare`: la Direzione decide da qui senza
 * dover vedere (e poter toccare) le gare. Usa solo `/api/acquisti/analisi`.
 *
 *   calcolo dal vivo   quello che verrebbe salvato adesso; si aggiorna normalizzando le offerte
 *   versione salvata   lo snapshot: i numeri di quel momento, non un ricalcolo
 *
 * L'Ufficio Acquisti salva la versione con la sua proposta; la Direzione la approva, scegliendo la
 * prima del ranking o — motivandolo — un'altra ammessa.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { analisiApi } from "../services/acquistiApi";
import { apriFile } from "../services/file";
import MatriceComparazione from "../components/MatriceComparazione";
import { AreaTesto, Campo, Caricamento, Avviso } from "../components/campi";
import {
  STATO_ANALISI,
  STATO_ANALISI_BADGE,
  ANALISI_BOZZA,
  ANALISI_APPROVATA,
} from "../constants/optionSet";
import { etichetta } from "../utils/formato";

export default function ComparazionePage() {
  const { id: garaId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canPerformAction, canAccessSection } = useAuth();
  const puoCalcolare = canPerformAction("analisi.gestisci");
  const puoApprovare = canPerformAction("analisi.approva");
  const puoContratto = canPerformAction("contratti.gestisci");

  const [versioni, setVersioni] = useState([]);
  const [dalVivo, setDalVivo] = useState(null);
  const [analisi, setAnalisi] = useState(null);
  const [proposta, setProposta] = useState("");
  const [scelta, setScelta] = useState("");
  const [motivazione, setMotivazione] = useState("");
  const [caricamento, setCaricamento] = useState(true);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);

  const selezionata = params.get("analisi") || "vivo";

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const [v, c] = await Promise.all([
        analisiApi.perGara(garaId),
        analisiApi
          .calcolo(garaId)
          .catch((e) => ({ errore: e.data?.message || e.message })),
      ]);
      setVersioni(v.value || []);
      setDalVivo(c);
      setErrore(null);
    } catch (e) {
      setErrore(e.data?.message || e.message);
    } finally {
      setCaricamento(false);
    }
  }, [garaId]);

  useEffect(() => {
    carica();
  }, [carica]);

  // Senza ?analisi= si apre la versione approvata, o l'ultima bozza, se ci sono.
  useEffect(() => {
    if (params.get("analisi") || !versioni.length) return;
    const preferita =
      versioni.find((v) => v.stato === ANALISI_APPROVATA) ||
      versioni.find((v) => v.stato === ANALISI_BOZZA);
    if (preferita) setParams({ analisi: preferita.id }, { replace: true });
  }, [versioni, params, setParams]);

  useEffect(() => {
    if (selezionata === "vivo") {
      setAnalisi(null);
      return;
    }
    analisiApi
      .get(selezionata)
      .then((a) => {
        setAnalisi(a);
        setScelta(a.snapshot?.ranking?.[0] || "");
        setMotivazione("");
      })
      .catch((e) => setErrore(e.message));
  }, [selezionata, versioni]);

  const comparazione = analisi
    ? analisi.snapshot
    : dalVivo?.errore
      ? null
      : dalVivo;
  const gara = comparazione?.gara || analisi?.snapshot?.gara;

  const ammesse = useMemo(
    () =>
      (analisi?.snapshot?.offerte || [])
        .filter((o) => !o.esclusa)
        .sort((a, b) => a.posizione - b.posizione),
    [analisi],
  );

  const azione = async (fn, messaggio) => {
    setInCorso(true);
    setErrore(null);
    try {
      const res = await fn();
      if (messaggio)
        showToast(
          typeof messaggio === "function" ? messaggio(res) : messaggio,
          "success",
        );
      return res;
    } catch (e) {
      setErrore(e.data?.message || e.message || "Operazione fallita");
      return null;
    } finally {
      setInCorso(false);
    }
  };

  const salvaVersione = async () => {
    const res = await azione(
      () => analisiApi.crea(garaId, proposta),
      (r) => r.message,
    );
    if (res) {
      setProposta("");
      await carica();
      setParams({ analisi: res.id });
    }
  };

  const approva = async () => {
    const primo = analisi.snapshot.ranking[0];
    const nome = ammesse.find((o) => o.offertaId === scelta)?.fornitore;
    if (
      !window.confirm(
        `Aggiudicare la gara a ${nome}?\n\nL'analisi diventerà immutabile e la gara Aggiudicata.`,
      )
    )
      return;
    const res = await azione(
      () =>
        analisiApi.approva(analisi.id, {
          offertaId: scelta,
          motivazione:
            scelta !== primo || motivazione ? motivazione : undefined,
        }),
      (r) => r.message,
    );
    if (res) {
      await carica();
      setParams({ analisi: analisi.id });
    }
  };

  const generaContratto = async () => {
    const res = await azione(
      () => analisiApi.generaContratto(analisi.id),
      "Contratto creato in definizione",
    );
    if (res) navigate(`/contratti/${res.id}`);
  };

  if (caricamento) return <Caricamento testo="Caricamento comparazione…" />;

  const primo = analisi?.snapshot?.ranking?.[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {canAccessSection("acquisti_gare") ? (
          <Link
            to={`/gare/${garaId}`}
            className="text-sm text-acquisti-700 hover:underline"
          >
            ← Gara
          </Link>
        ) : (
          <Link
            to="/analisi"
            className="text-sm text-acquisti-700 hover:underline"
          >
            ← Analisi
          </Link>
        )}
        <h1 className="text-xl font-semibold text-gray-900">
          Comparazione {gara ? `— ${gara.codice} ${gara.nome}` : ""}
        </h1>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={selezionata === "vivo" ? "btn-primary" : "btn-secondary"}
          onClick={() => setParams({})}
        >
          Calcolo dal vivo
        </button>
        {versioni.map((v) => (
          <button
            key={v.id}
            type="button"
            className={selezionata === v.id ? "btn-primary" : "btn-secondary"}
            onClick={() => setParams({ analisi: v.id })}
          >
            v{v.versione} · {v.statoLabel}
          </button>
        ))}
      </div>

      {selezionata === "vivo" && (
        <>
          {dalVivo?.errore && (
            <Avviso tipo="attenzione">{dalVivo.errore}</Avviso>
          )}
          <Avviso tipo="info">
            Calcolo dal vivo: cambia se si correggono le offerte. Quello che
            vale per la decisione è la versione salvata.
            {dalVivo?.valutazioneUscenteUsata &&
              " Le voci collegate a un criterio di valutazione usano, per il fornitore uscente, l'ultima valutazione approvata."}
          </Avviso>
          {puoCalcolare &&
            gara?.stato === 100000002 &&
            !versioni.some((v) => v.stato === ANALISI_APPROVATA) && (
              <section className="card space-y-3">
                <Campo
                  label="Proposta per la Direzione"
                  aiuto="Il perché della scelta che proponi; resta nell'analisi."
                >
                  <AreaTesto valore={proposta} onChange={setProposta} />
                </Campo>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={inCorso}
                  onClick={salvaVersione}
                >
                  Salva versione per la Direzione
                </button>
              </section>
            )}
          {puoCalcolare &&
            gara &&
            gara.stato !== 100000002 &&
            gara.stato !== 100000003 && (
              <Avviso tipo="attenzione">
                Per salvare una versione la gara dev'essere «In valutazione»:
                chiudi la raccolta offerte dalla pagina della gara.
              </Avviso>
            )}
        </>
      )}

      {analisi && (
        <section className="card space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold text-gray-900">
              Analisi v{analisi.versione}
            </h2>
            <span
              className={STATO_ANALISI_BADGE[analisi.stato] || "badge-gray"}
            >
              {etichetta(STATO_ANALISI, analisi.stato)}
            </span>
            <span className="text-sm text-gray-500">
              salvata da {analisi.creataDa || "—"} il{" "}
              {new Date(
                analisi.snapshot?.calcolatoIl || analisi.creataIl,
              ).toLocaleString("it-IT")}
            </span>
            {analisi.stato === ANALISI_APPROVATA && (
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    apriFile(analisiApi.pdf(analisi.id)).catch((e) =>
                      setErrore(e.message),
                    )
                  }
                >
                  PDF dell'analisi
                </button>
                {analisi.contrattoGenerato ? (
                  canAccessSection("acquisti_contratti") ? (
                    <Link to={`/contratti/${analisi.contrattoGenerato.id}`} className="btn-secondary">
                      Contratto: {analisi.contrattoGenerato.nome}
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-600 self-center">
                      Contratto: {analisi.contrattoGenerato.nome}
                    </span>
                  )
                ) : (
                  puoContratto && (
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={inCorso}
                      onClick={generaContratto}
                    >
                      Genera il contratto
                    </button>
                  )
                )}
              </div>
            )}
          </div>
          {analisi.proposta && (
            <p className="text-sm">
              <strong>Proposta dell'Ufficio Acquisti:</strong>{" "}
              {analisi.proposta}
            </p>
          )}
          {analisi.stato === ANALISI_APPROVATA && (
            <p className="text-sm">
              <strong>Aggiudicata a {analisi.fornitoreVincitore?.nome}</strong>{" "}
              ({analisi.punteggioVincente} / 100) da {analisi.approvataDa} il{" "}
              {new Date(analisi.dataApprovazione).toLocaleDateString("it-IT")}
              {analisi.motivazione ? ` — ${analisi.motivazione}` : ""}
            </p>
          )}
          {analisi.stato === ANALISI_BOZZA && puoApprovare && (
            <div className="border-t pt-3 space-y-3">
              <h3 className="font-medium">Decisione</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Campo label="Offerta da aggiudicare">
                  <select
                    className="input"
                    value={scelta}
                    onChange={(e) => setScelta(e.target.value)}
                  >
                    {ammesse.map((o) => (
                      <option key={o.offertaId} value={o.offertaId}>
                        #{o.posizione} {o.fornitore} — {o.totale}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo
                  label={`Motivazione${scelta !== primo ? " (obbligatoria: non è la prima del ranking)" : ""}`}
                >
                  <AreaTesto
                    valore={motivazione}
                    onChange={setMotivazione}
                    righe={2}
                  />
                </Campo>
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={inCorso || (scelta !== primo && !motivazione.trim())}
                onClick={approva}
              >
                Approva e aggiudica
              </button>
            </div>
          )}
        </section>
      )}

      <section className="card !p-0">
        <MatriceComparazione
          comparazione={comparazione}
          evidenzia={analisi?.offertaVincenteId}
        />
      </section>
    </div>
  );
}
